import { Fragment } from "react";
import Link from "next/link";
import { TERMS_URL } from "@/config/routes";

// Single source of truth for the legal-doc links. Rendered by the global
// <Footer/> and, on the bottom-nav tab shells where the footer is suppressed
// (T-182 P6), inline on the profile pages. Keep the route list here so the
// three surfaces can't drift.
//
// The entry points at the external, client-hosted page (T-223 Terms) and so
// must open in a new tab via a plain <a> — the external branch below handles
// target/rel.
const LEGAL_LINKS = [{ href: TERMS_URL, label: "Terms & Conditions", external: true }] as const;

const linkClass =
  "inline-flex items-center min-h-11 px-2 hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#11471B]";

/**
 * Accessible row of legal-doc links. Text size/colour is inherited from the
 * caller (the footer sets text-sm/gray-600; the profile pages pass
 * `text-xs text-gray-600`) so the same markup reads correctly in both places.
 */
export default function LegalLinks({ className = "" }: { className?: string }) {
  return (
    <nav
      aria-label="Legal"
      className={`flex flex-wrap justify-center gap-x-2 gap-y-1 ${className}`.trim()}
    >
      {LEGAL_LINKS.map((link, i) => (
        <Fragment key={link.href}>
          {i > 0 && (
            <span aria-hidden="true" className="self-center">
              ·
            </span>
          )}
          {link.external ? (
            <a href={link.href} target="_blank" rel="noopener noreferrer" className={linkClass}>
              {link.label}
            </a>
          ) : (
            <Link href={link.href} className={linkClass}>
              {link.label}
            </Link>
          )}
        </Fragment>
      ))}
    </nav>
  );
}
